import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut,
  User,
  Briefcase,
  Clock,
  Target,
  Package,
  Eye,
  Check,
  Search,
  Play,
} from "lucide-react";
import "./dashboard.css";
import BackButton from "../../components/backbutton";

export default function EmployeeDashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("profile-detail");
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProcess, setSelectedProcess] = useState(null);
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [processOutput, setProcessOutput] = useState({
    accepted: "",
    nc: "",
    rejected: "",
    scrap: ""
  });

  // Employee Profile Data
  const [employeeProfile] = useState({
    employeeCode: "EMP-001",
    employeeName: "John Doe",
    employeeDesignation: "Senior Production Engineer",
    department: "Production",
    category: "Full Time",
    joinDate: "2022-01-15",
    email: "john.doe@company.com",
    phone: "+91 98765 43210"
  });

  // Assigned Work Data
  const [assignedWork] = useState([
    { 
      id: "WORK-001", 
      processName: "Cutting Process", 
      processDetails: "Cutting raw materials as per specifications",
      product: "Product A",
      quantity: 100,
      startDate: "2024-04-01",
      endDate: "2024-04-10",
      status: "In Progress",
      machine: "Cutting Machine",
      tool: "Blade",
      employee: "John Doe",
      instrument: "Caliper",
      accessories: ["Safety Gloves", "Goggles"],
      cost: "₹500"
    },
    { 
      id: "WORK-002", 
      processName: "Assembly Process", 
      processDetails: "Assembling components",
      product: "Product B",
      quantity: 50,
      startDate: "2024-04-05",
      endDate: "2024-04-15",
      status: "Pending",
      machine: "Assembly Line",
      tool: "Screwdriver",
      employee: "John Doe",
      instrument: "Torque Wrench",
      accessories: ["Screws", "Brackets"],
      cost: "₹1,000"
    }
  ]);

  // Process Data (Production Root Sheet)
  const [processes, setProcesses] = useState([
    {
      id: "PROC-001",
      product: "Product A",
      processName: "Cutting Process",
      processId: "PR-001",
      processDetails: "Cut raw materials to specified dimensions",
      checklist: ["Safety check", "Material verification", "Machine setup"],
      status: "Not Started",
      machine: "Cutting Machine",
      tool: "Blade",
      employee: "John Doe",
      instrument: "Caliper",
      accessories: ["Safety Gloves", "Goggles"],
      cost: "₹500"
    },
    {
      id: "PROC-002",
      product: "Product A",
      processName: "Assembly Process",
      processId: "PR-002",
      processDetails: "Assemble all components together",
      checklist: ["Component check", "Alignment verification", "Quality check"],
      status: "Not Started",
      machine: "Assembly Line",
      tool: "Screwdriver",
      employee: "John Doe",
      instrument: "Torque Wrench",
      accessories: ["Screws", "Brackets"],
      cost: "₹1,000"
    }
  ]);

  // Pending Tasks
  const [pendingTasks] = useState([
    { id: "TASK-001", task: "Complete Cutting Process for Product A", deadline: "2024-04-10", priority: "High" },
    { id: "TASK-002", task: "Quality check for assembled units", deadline: "2024-04-12", priority: "Medium" }
  ]);

  // Target Dates
  const [targetDates] = useState([
    { id: "TGT-001", product: "Product A", targetQuantity: 100, completedQuantity: 45, targetDate: "2024-04-10", status: "In Progress" },
    { id: "TGT-002", product: "Product B", targetQuantity: 50, completedQuantity: 20, targetDate: "2024-04-15", status: "In Progress" }
  ]);

  // Process Output History
  const [processOutputs, setProcessOutputs] = useState([
    { id: "OUT-001", product: "Product A", process: "Cutting Process", accepted: 95, nc: 2, rejected: 2, scrap: 1, date: "2024-04-08" }
  ]);

  // Sidebar menu items
  const tabItems = [
    { id: "profile-detail", label: "Profile Detail", icon: <User size={18} /> },
    { id: "assigned-work", label: "Assigned Work", icon: <Briefcase size={18} /> },
    { id: "process", label: "Process", icon: <Package size={18} /> },
    { id: "pending", label: "Pending", icon: <Clock size={18} /> },
    { id: "target-date", label: "Target Date", icon: <Target size={18} /> },
    { id: "product-process-output", label: "Product Process Output", icon: <Package size={18} /> }
  ];

  const handleLogoutClick = () => setShowLogoutModal(true);
  const handleConfirmLogout = () => {
    setShowLogoutModal(false);
    onLogout();
    navigate('/login');
  };
  const handleCancelLogout = () => setShowLogoutModal(false);

  // const getPageTitle = () => {
  //   switch (activeTab) {
  //     case "profile-detail": return "Profile Detail";
  //     case "assigned-work": return "Assigned Work";
  //     case "process": return "Process / Production Root Sheet";
  //     case "pending": return "Pending Tasks";
  //     case "target-date": return "Target Date";
  //     case "product-process-output": return "Product Process Output";
  //     default: return "Employee Dashboard";
  //   }
  // };

  // Handle Start Process
  const handleStartProcess = (process) => {
    setSelectedProcess(process);
    setCurrentStep(1);
    setShowProcessModal(true);
  };

  // Handle Process Completion
  const handleCompleteProcess = () => {
    if (selectedProcess) {
      setProcesses(processes.map(p => 
        p.id === selectedProcess.id ? { ...p, status: "Completed" } : p
      ));
      setProcessOutputs([...processOutputs, {
        id: `OUT-${Date.now()}`,
        product: selectedProcess.product,
        process: selectedProcess.processName,
        accepted: parseInt(processOutput.accepted) || 0,
        nc: parseInt(processOutput.nc) || 0,
        rejected: parseInt(processOutput.rejected) || 0,
        scrap: parseInt(processOutput.scrap) || 0,
        date: new Date().toISOString().split('T')[0]
      }]);
      alert(`Process ${selectedProcess.processName} completed successfully!`);
      setShowProcessModal(false);
      setProcessOutput({ accepted: "", nc: "", rejected: "", scrap: "" });
    }
  };

  // Filter function
  const filteredData = (data, searchFields) => {
    if (!searchTerm) return data;
    return data.filter(item => 
      searchFields.some(field => 
        item[field]?.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  };

  // Profile Detail Component
  const ProfileDetailContent = () => (
    <div className="content-container">
      <div className="profile-card">
        <div className="profile-header">
          <div className="profile-avatar">
            <div className="avatar-placeholder">
              <User size={48} />
            </div>
          </div>
          <div className="profile-basic-info">
            <h2>{employeeProfile.employeeName}</h2>
            <p>{employeeProfile.employeeDesignation}</p>
            <p className="dept">{employeeProfile.department}</p>
          </div>
        </div>
        <div className="profile-details-grid">
          <div className="detail-field"><label>Employee Code:</label><span>{employeeProfile.employeeCode}</span></div>
          <div className="detail-field"><label>Employee Name:</label><span>{employeeProfile.employeeName}</span></div>
          <div className="detail-field"><label>Designation:</label><span>{employeeProfile.employeeDesignation}</span></div>
          <div className="detail-field"><label>Department:</label><span>{employeeProfile.department}</span></div>
          <div className="detail-field"><label>Category:</label><span>{employeeProfile.category}</span></div>
          <div className="detail-field"><label>Join Date:</label><span>{employeeProfile.joinDate}</span></div>
          <div className="detail-field"><label>Email:</label><span>{employeeProfile.email}</span></div>
          <div className="detail-field"><label>Phone:</label><span>{employeeProfile.phone}</span></div>
        </div>
        <button className="btn-add-fields" onClick={() => alert("Add additional fields")}>+ Add Fields</button>
      </div>
    </div>
  );

  // Assigned Work Component
  const AssignedWorkContent = () => (
    <div className="content-container">
      <div className="filter-bar">
        <Search size={18} />
        <input type="text" placeholder="Search assigned work..." className="search-input" 
          value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
      </div>
      <div className="work-list">
        {filteredData(assignedWork, ['processName', 'product', 'employee']).map(work => (
          <div key={work.id} className="work-card">
            <div className="work-header">
              <h3>{work.processName}</h3>
              <span className={`status ${work.status === 'In Progress' ? 'approved' : 'pending'}`}>{work.status}</span>
            </div>
            <div className="work-details">
              <p><strong>Process Details:</strong> {work.processDetails}</p>
              <p><strong>Product:</strong> {work.product} | <strong>Quantity:</strong> {work.quantity}</p>
              <p><strong>Timeline:</strong> {work.startDate} to {work.endDate}</p>
              <div className="process-info">
                <p><strong>Machine:</strong> {work.machine} | <strong>Tool:</strong> {work.tool}</p>
                <p><strong>Employee:</strong> {work.employee} | <strong>Instrument:</strong> {work.instrument}</p>
                <p><strong>Accessories:</strong> {work.accessories.join(', ')}</p>
                <p><strong>Cost:</strong> {work.cost}</p>
              </div>
            </div>
            <div className="work-actions">
              {work.status === 'Pending' && (
                <button className="action-btn approve" onClick={() => handleStartProcess(work)}>
                  <Play size={16} /> Start Process
                </button>
              )}
              {work.status === 'In Progress' && (
                <button className="action-btn edit" onClick={() => alert("Process in progress...")}>
                  <Clock size={16} /> In Progress
                </button>
              )}
              <button className="action-btn view" onClick={() => alert(`View details of ${work.processName}`)}>
                <Eye size={16} /> View Details
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Process Component (Production Root Sheet)
  const ProcessContent = () => (
    <div className="content-container">
      <div className="root-sheet-container">
        <h3>Production Root Sheet</h3>
        {processes.map(process => (
          <div key={process.id} className="process-root-card">
            <div className="process-root-header">
              <h4>{process.product} - {process.processName}</h4>
              <span className={`status ${process.status === 'Completed' ? 'approved' : 'pending'}`}>{process.status}</span>
            </div>
            <div className="process-root-details">
              <div className="detail-row"><label>Machine:</label><span>{process.machine}</span></div>
              <div className="detail-row"><label>Tool:</label><span>{process.tool}</span></div>
              <div className="detail-row"><label>Employee:</label><span>{process.employee}</span></div>
              <div className="detail-row"><label>Instrument:</label><span>{process.instrument}</span></div>
              <div className="detail-row"><label>Accessories:</label><span>{process.accessories.join(', ')}</span></div>
              <div className="detail-row"><label>Cost:</label><span className="amount">{process.cost}</span></div>
            </div>
            {process.status !== 'Completed' && (
              <button className="btn-start" onClick={() => handleStartProcess(process)}>
                <Play size={16} /> Start Process
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  // Pending Component
  const PendingContent = () => (
    <div className="content-container">
      <div className="pending-list">
        {pendingTasks.map(task => (
          <div key={task.id} className="pending-card">
            <div className="pending-info">
              <h4>{task.task}</h4>
              <p>Deadline: {task.deadline}</p>
              <span className={`status ${task.priority === 'High' ? 'pending' : 'approved'}`}>{task.priority} Priority</span>
            </div>
            <button className="action-btn view" onClick={() => alert(`Mark task as complete: ${task.task}`)}>
              <Check size={16} /> Mark Complete
            </button>
          </div>
        ))}
      </div>
    </div>
  );

  // Target Date Component
  const TargetDateContent = () => (
    <div className="content-container">
      <div className="target-list">
        {targetDates.map(target => {
          const progress = (target.completedQuantity / target.targetQuantity) * 100;
          return (
            <div key={target.id} className="target-card">
              <div className="target-header">
                <h4>{target.product}</h4>
                <span className={`status ${target.status === 'Completed' ? 'approved' : 'pending'}`}>{target.status}</span>
              </div>
              <div className="target-details">
                <p>Target Quantity: {target.targetQuantity}</p>
                <p>Completed: {target.completedQuantity}</p>
                <p>Pending: {target.targetQuantity - target.completedQuantity}</p>
                <p>Target Date: {target.targetDate}</p>
              </div>
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
                <span className="progress-text">{progress.toFixed(0)}%</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // Product Process Output Component
  const ProductProcessOutputContent = () => (
    <div className="content-container">
      <div className="filter-bar">
        <Search size={18} />
        <input type="text" placeholder="Search outputs..." className="search-input" 
          value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
      </div>
      <div className="data-table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Product</th>
              <th>Process</th>
              <th>Accepted</th>
              <th>NC</th>
              <th>Rejected</th>
              <th>Scrap</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredData(processOutputs, ['product', 'process']).map(output => (
              <tr key={output.id}>
                <td>{output.date}</td>
                <td className="highlight">{output.product}</td>
                <td>{output.process}</td>
                <td className="approved">{output.accepted}</td>
                <td className="pending">{output.nc}</td>
                <td className="rejected">{output.rejected}</td>
                <td className="pending">{output.scrap}</td>
                <td><button className="action-btn view" onClick={() => alert(`View details for ${output.process}`)}><Eye size={16} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // Process Modal (4-step wizard)
  const ProcessModal = () => (
    <div className="modal-overlay" onClick={() => setShowProcessModal(false)}>
      <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>List Process ({currentStep}/4) - {selectedProcess?.processName}</h2>
          <button className="modal-close" onClick={() => setShowProcessModal(false)}>×</button>
        </div>
        <div className="modal-body">
          {currentStep === 1 && (
            <div className="step-content">
              <div className="form-group"><label>Product:</label><input type="text" className="form-input" value={selectedProcess?.product} readOnly /></div>
              <div className="form-group"><label>Process Name:</label><input type="text" className="form-input" value={selectedProcess?.processName} readOnly /></div>
              <div className="form-group"><label>Process ID:</label><input type="text" className="form-input" placeholder="Process ID" /></div>
            </div>
          )}
          {currentStep === 2 && (
            <div className="step-content">
              <div className="form-group"><label>Process Detail:</label><textarea className="form-textarea" rows="4" placeholder="Enter process details..." defaultValue={selectedProcess?.processDetails}></textarea></div>
            </div>
          )}
          {currentStep === 3 && (
            <div className="step-content">
              <div className="checklist-group">
                <h4>Checklist</h4>
                {selectedProcess?.checklist?.map((item, idx) => (
                  <label key={idx} className="checkbox-label">
                    <input type="checkbox" /> {item}
                  </label>
                ))}
              </div>
              <div className="form-group"><label>Data / Notes:</label><textarea className="form-textarea" rows="3" placeholder="Additional data..."></textarea></div>
            </div>
          )}
          {currentStep === 4 && (
            <div className="step-content">
              <div className="output-grid">
                <div className="form-group"><label>Quantity Accepted:</label><input type="number" className="form-input" placeholder="Accepted" value={processOutput.accepted} onChange={(e) => setProcessOutput({...processOutput, accepted: e.target.value})} /></div>
                <div className="form-group"><label>NC (Non-Conforming):</label><input type="number" className="form-input" placeholder="NC" value={processOutput.nc} onChange={(e) => setProcessOutput({...processOutput, nc: e.target.value})} /></div>
                <div className="form-group"><label>Rejected:</label><input type="number" className="form-input" placeholder="Rejected" value={processOutput.rejected} onChange={(e) => setProcessOutput({...processOutput, rejected: e.target.value})} /></div>
                <div className="form-group"><label>Scrap:</label><input type="number" className="form-input" placeholder="Scrap" value={processOutput.scrap} onChange={(e) => setProcessOutput({...processOutput, scrap: e.target.value})} /></div>
              </div>
            </div>
          )}
        </div>
        <div className="modal-footer">
          {currentStep > 1 && <button className="btn-secondary" onClick={() => setCurrentStep(currentStep - 1)}>Previous</button>}
          {currentStep < 4 ? (
            <button className="btn-approve" onClick={() => setCurrentStep(currentStep + 1)}>Next</button>
          ) : (
            <button className="btn-approve" onClick={handleCompleteProcess}>Complete Process</button>
          )}
          <button className="btn-cancel" onClick={() => setShowProcessModal(false)}>Cancel</button>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case "profile-detail": return <ProfileDetailContent />;
      case "assigned-work": return <AssignedWorkContent />;
      case "process": return <ProcessContent />;
      case "pending": return <PendingContent />;
      case "target-date": return <TargetDateContent />;
      case "product-process-output": return <ProductProcessOutputContent />;
      default: return <ProfileDetailContent />;
    }
  };

  return (
    <div className="employee-dashboard-container">
      <div className="employee-sidebar">
        <div className="employee-logo-container">
          <div className="employee-logo-icon">
            <img src="/rahulerp.jpg" alt="Company Logo" className="employee-logo-image" />
          </div>
          <div>
            <h2 className="employee-company-name">Ragul Auto Tech</h2>
            <p className="employee-company-tagline">AI Powered ERP</p>
          </div>
        </div>
        <nav className="employee-nav-menu">
          {tabItems.map((tab) => (
            <button key={tab.id} className={`employee-nav-item ${activeTab === tab.id ? 'employee-nav-item-active' : ''}`} onClick={() => setActiveTab(tab.id)}>
              <span className="employee-nav-icon">{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
        <div className="employee-logout-container">
          <button onClick={handleLogoutClick} className="employee-logout-button">
            <LogOut size={18} /> Logout
          </button>
        </div>
      </div>

      <div className="employee-main-content">
        <header className="employee-header">
          <div>
            <h1 className="employee-main-title">Employee Dashboard</h1>
            <div className="employee-subtitle">Welcome, {user?.role || 'Employee'} - {employeeProfile.employeeName}</div>
          </div>
          <div className="employee-header-actions">
            <div className="employee-last-updated">Last updated: {new Date().toLocaleString()}</div>
          </div>
        </header>
        <BackButton />
        <div className="employee-content-card">
          {renderContent()}
        </div>
        {showLogoutModal && (
          <div className="logout-modal-overlay">
            <div className="logout-modal">
              <div className="logout-modal-header">
                <div className="logout-modal-icon">👋</div>
                <h3>Confirm Logout</h3>
              </div>
              <div className="logout-modal-body">
                <p>Are you sure you want to logout from <strong>Employee</strong> dashboard?</p>
                <p className="logout-modal-warning">You will be redirected to the login page.</p>
              </div>
              <div className="logout-modal-footer">
                <button className="logout-modal-cancel" onClick={handleCancelLogout}>Cancel</button>
                <button className="logout-modal-confirm" onClick={handleConfirmLogout}>Yes, Logout</button>
              </div>
            </div>
          </div>
        )}
      </div>
      {showProcessModal && selectedProcess && <ProcessModal />}
    </div>
  );
}